import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n/index.js';
import { API_BASE_URL } from '../config/apiMode';

const API = axios.create({ baseURL: API_BASE_URL });
const Ctx = createContext();
const ANON_SESSION_STORAGE_KEY = 'fb_anonymous_session_token';

function getAnonymousSessionToken() {
  if (typeof window === 'undefined') return null;

  let token = window.sessionStorage.getItem(ANON_SESSION_STORAGE_KEY);
  if (!token) {
    token = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(ANON_SESSION_STORAGE_KEY, token);
  }
  return token;
}

export function AppProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [token, setToken]         = useState(localStorage.getItem('fb_token'));
  const [capexConfig, setCapexConfig] = useState(null);
  const [opexResult, setOpexResult]   = useState(null);
  const [language, setLanguageState]  = useState(localStorage.getItem('fb_lang') || i18n.language || 'en');

  API.defaults.headers.common['Accept-Language'] = language;

  // Ensure auth header is available immediately on refresh, before child effects run.
  if (token) {
    API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  const setLanguage = (lang) => {
    localStorage.setItem('fb_lang', lang);
    i18n.changeLanguage(lang);
    API.defaults.headers.common['Accept-Language'] = lang;
    setLanguageState(lang);
  };

  useEffect(() => {
    if (token) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      API.get('/api/auth/me').then(r => setUser(r.data)).catch(() => logout());
    }
  }, [token]);

  useEffect(() => {
    API.defaults.headers.common['Accept-Language'] = language;
  }, [language]);

  const login = async (email, password) => {
    const r = await API.post('/api/auth/login', { email, password });
    localStorage.setItem('fb_token', r.data.token);
    API.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    setToken(r.data.token); setUser(r.data.user);
    return r.data.user; // ← ritorna direttamente user (con role)
  };

  const register = async (data) => {
    const r = await API.post('/api/auth/register', data);
    localStorage.setItem('fb_token', r.data.token);
    API.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
    setToken(r.data.token); setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem('fb_token'); setToken(null); setUser(null);
    delete API.defaults.headers.common['Authorization'];
  };

  const calculateOpex  = (type, params) => API.post(`/api/opex/${type}`, params).then(r => r.data);
  const calculateCapex = (params) => API.post('/api/capex/calculate', params).then(r => r.data);
  const saveCapex      = (params) => API.post('/api/capex/save', params).then(r => r.data);
  const saveScenario   = (params) => API.post('/api/scenarios/save', params).then(r => r.data);
  const trackScenario  = (params = {}) => API.post('/api/scenarios/track', {
    ...params,
    anonymousSessionToken: params.anonymousSessionToken || (!token ? getAnonymousSessionToken() : undefined)
  }).then(r => r.data);
  const sendLandingInquiry = (params) => API.post('/api/contact/landing-inquiry', params).then(r => r.data);
  const generateBP     = (params) => API.post('/api/business-plan/generate', params).then(r => r.data);
  const listScenarios  = () => API.get('/api/scenarios/list').then(r => r.data);
  const listBPs        = () => API.get('/api/business-plan/list').then(r => r.data);
  const listCapex      = () => API.get('/api/capex/list').then(r => r.data);
  const listOpex       = () => API.get('/api/scenarios/list').then(r => r.data);
  const deleteBP       = (id) => API.delete(`/api/business-plan/${id}`).then(r => r.data);
  const fetchDashboard = () => API.get('/api/dashboard/summary').then(r => r.data);
  const saveDiscoveryIntake = (params) => API.post('/api/discovery/save', params).then(r => r.data);
  const listDiscoveryIntakes = () => API.get('/api/discovery/list').then(r => r.data);

  return (
    <Ctx.Provider value={{
      user, token, login, register, logout,
      capexConfig, setCapexConfig,
      opexResult, setOpexResult,
      calculateOpex, calculateCapex, saveCapex, saveScenario, trackScenario, sendLandingInquiry,
      generateBP, listScenarios, listBPs,
      listCapex, listOpex, deleteBP, fetchDashboard,
      saveDiscoveryIntake, listDiscoveryIntakes,
      language, setLanguage
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useApp = () => useContext(Ctx);
